/**
 * Comment moderation service — the Brooklyn admin comments list, comment
 * update, and the moderation status vocabulary.
 * @module services/comments
 */
import { fromJS } from 'immutable'
import get from 'lodash/get'
import request from '../request'
import { createAuthorization } from '../auth'

/**
 * Comment moderation statuses. These mirror Brooklyn's
 * `lib/services/comments/status.js` constants and MUST stay in sync with them —
 * `comments.status` is an inverted SMALLINT where PUBLISHED is 0 and anything
 * truthy is hidden from normal members.
 *
 * PENDING_REVIEW (3) and REJECTED (4) split the two facts that UNPUBLISHED (1)
 * used to conflate: "created, never reviewed" vs "reviewed and denied". Legacy
 * rows are NOT migrated, so 1 keeps its honest ambiguity — legacy backlog or a
 * post-publication takedown — and is labelled "Not published" for both.
 *
 * AI_REJECTED (5) will be written by Brooklyn's automated moderation pipeline
 * (not yet live). A moderator either overturns it (Publish) or confirms it,
 * converting the machine decision into a human REJECTED (4) — no admin action
 * writes 5.
 */
export const STATUS_PUBLISHED = 0
export const STATUS_UNPUBLISHED = 1
export const STATUS_NEEDS_REVIEW = 2
export const STATUS_PENDING_REVIEW = 3
export const STATUS_REJECTED = 4
export const STATUS_AI_REJECTED = 5

/**
 * Display label per status. Every known status is enumerated so an unrecognised
 * value is reported as unknown rather than silently mislabelled.
 */
export const STATUS_LABELS = {
  [STATUS_PUBLISHED]: 'Published',
  [STATUS_UNPUBLISHED]: 'Not published',
  [STATUS_NEEDS_REVIEW]: 'Needs review',
  [STATUS_PENDING_REVIEW]: 'Pending review',
  [STATUS_REJECTED]: 'Rejected',
  [STATUS_AI_REJECTED]: 'AI rejected',
}

/**
 * Get the display label for a comment status
 * @param {number} status The comment status
 * @returns {string} The label, or an explicit unknown marker
 */
export function getStatusLabel (status) {
  return STATUS_LABELS[status] || `Unknown status (${status})`
}

/**
 * The moderation transitions offered for each status, one explicit case per
 * status. There is deliberately NO fallback: an unrecognised status offers no
 * actions rather than guessing a transition on the moderator's behalf.
 *
 * UNPUBLISHED (1) keeps both Publish and Reject. That is how the legacy backlog
 * gets triaged into the new vocabulary over time, since those rows were never
 * migrated and no migration could tell "never reviewed" from "rejected".
 *
 * AI_REJECTED (5) offers the same pair as PENDING_REVIEW: the human either
 * overturns the machine (Publish) or confirms it, and confirming converts the
 * decision into a human REJECTED (4).
 */
export const STATUS_ACTIONS = {
  [STATUS_PUBLISHED]: [{ label: 'Unpublish', status: STATUS_UNPUBLISHED }],
  [STATUS_PENDING_REVIEW]: [
    { label: 'Publish', status: STATUS_PUBLISHED },
    { label: 'Reject', status: STATUS_REJECTED },
  ],
  [STATUS_REJECTED]: [{ label: 'Publish', status: STATUS_PUBLISHED }],
  [STATUS_AI_REJECTED]: [
    { label: 'Publish', status: STATUS_PUBLISHED },
    { label: 'Reject', status: STATUS_REJECTED },
  ],
  [STATUS_UNPUBLISHED]: [
    { label: 'Publish', status: STATUS_PUBLISHED },
    { label: 'Reject', status: STATUS_REJECTED },
  ],
  [STATUS_NEEDS_REVIEW]: [
    { label: 'Publish', status: STATUS_PUBLISHED },
    { label: 'Unpublish', status: STATUS_UNPUBLISHED },
  ],
}

/**
 * Get the moderation actions available for a comment status
 * @param {number} status The comment status
 * @returns {Array<{label: string, status: number}>} The available actions; empty for an unrecognised status
 */
export function getStatusActions (status) {
  return STATUS_ACTIONS[status] || []
}

/**
 * Get a list of comments
 * @param {object} options The options
 * @param {string} options.server The server url
 * @param {Map} options.auth An immutable map with the auth data
 * @param {number} options.offset The query offset
 * @param {number} options.limit The query limit
 */
export async function getCommentsList (options = {}) {
  const { server, auth, offset, limit, uid, contentId, status } = options
  const res = await request
    .get(`${server}/v1/admin/comments/list`)
    .query({
      offset,
      limit,
      uid,
      contentId,
      status,
    })
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(get(res, 'body', {}))
}

/**
 * Update a comment, allows partial update
 * @param {object} options The options
 * @param {string} options.server The server url
 * @param {Map} options.auth An immutable map with the auth data
 * @param {number} options.id The comment id
 * @param {Map} options.data The PUT request body
 */
export async function update (options = {}) {
  const { server, auth, id, data } = options
  const res = await request
    .put(`${server}/v1/comments/${id}`)
    .send(data.toJS())
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(get(res, 'body', {}))
}
